import { useApi } from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import type {
  NoticeInput,
  NoticeUpdate,
} from '@internal/backstage-plugin-neo4j-notices-common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatApiError } from '../errors';
import { noticesApiRef } from '../types';
import { noticesQueryKey } from './useNotices';

const SUCCESS_TOAST_DURATION_MS = 5_000;

export function useCreateNotice() {
  const api = useApi(noticesApiRef);
  const toastApi = useApi(toastApiRef);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: NoticeInput) => api.createNotice(input),
    onSuccess: async notice => {
      await queryClient.invalidateQueries({
        queryKey: noticesQueryKey,
      });
      toastApi.post({
        title: notice.incidentId
          ? `Published notice for ${notice.incidentId}`
          : 'Published notice',
        status: 'success',
        timeout: SUCCESS_TOAST_DURATION_MS,
      });
    },
    onError: (error: Error) => {
      toastApi.post({
        title: 'Failed to publish notice',
        description: formatApiError(error).message,
        status: 'danger',
      });
    },
  });
}

export function useUpdateNotice() {
  const api = useApi(noticesApiRef);
  const toastApi = useApi(toastApiRef);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, update }: { id: string; update: NoticeUpdate }) =>
      api.updateNotice(id, update),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: noticesQueryKey,
      });
      toastApi.post({
        title: 'Updated notice',
        status: 'success',
        timeout: SUCCESS_TOAST_DURATION_MS,
      });
    },
    onError: (error: Error) => {
      toastApi.post({
        title: 'Failed to update notice',
        description: formatApiError(error).message,
        status: 'danger',
      });
    },
  });
}

export function useDeleteNotice() {
  const api = useApi(noticesApiRef);
  const toastApi = useApi(toastApiRef);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteNotice(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: noticesQueryKey,
      });
      toastApi.post({
        title: 'Removed notice',
        status: 'success',
        timeout: SUCCESS_TOAST_DURATION_MS,
      });
    },
    onError: (error: Error) => {
      toastApi.post({
        title: 'Failed to remove notice',
        description: formatApiError(error).message,
        status: 'danger',
      });
    },
  });
}

export function useDismissNotice() {
  const api = useApi(noticesApiRef);
  const toastApi = useApi(toastApiRef);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.dismissNotice(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: noticesQueryKey,
      });
    },
    onError: (error: Error) => {
      toastApi.post({
        title: 'Failed to dismiss notice',
        description: formatApiError(error).message,
        status: 'danger',
      });
    },
  });
}
